import { createRef, RefObject } from 'react';
import { classNames } from '@progress/kendo-react-common';
import { 
    ExternalDropZone, 
    Upload, 
    UploadOnAddEvent,
    UploadOnRemoveEvent,
    UploadFileInfo,
    UploadFileRestrictions
} from '@progress/kendo-react-upload';
import styles from './FileUpload.module.scss';

const defaultExtensions = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.txt', 
    '.rtf',
    '.doc',
    '.docx',
    '.odt',
    '.csv',
    '.xls',
    '.xlsx',
    '.tif',
    '.tiff', 
    '.svg',
    '.gif',
    '.xml',
    '.ppt',
    '.pptx',
    '.zip',
    '.log',
    '.bin',
    '.dat',
    '.bmp',
    '.html',
    '.eml',
    '.msg'
];

const defaultFileSize = 25000000 // 25MB;

const defaultRestrictions = {
    allowedExtensions: defaultExtensions,
    maxFileSize: defaultFileSize
};

const uploadRef: RefObject<Upload> = createRef<Upload>();
const hint: JSX.Element = <span className={styles.customUploadHint}>Drag files here to upload</span>;
const note: JSX.Element = <span className={styles.customUploadNote}>or click the "Select files..." button</span>;

type FileUploadProps = {
    className?: string,
    id: string,
    onBlur: () => void,
    onChange: (event: {
        target?: any;
        value?: any;
    }) => void,
    restrictions?: UploadFileRestrictions,
    value: UploadFileInfo[],
};

export default function FileUpload(props: FileUploadProps) {
    const {
        className,
        id,
        onBlur,
        onChange, 
        restrictions = defaultRestrictions,
        value,
        ...rest
    } = props;

    const onAdd = (event: UploadOnAddEvent) => {
        onChange && onChange({
            target: event.target,
            value: event.newState}
        );
        onBlur && onBlur();
    };

    const onRemove = (event: UploadOnRemoveEvent) => {
        onChange && onChange({
            target: event.target,
            value: event.newState}
        );
    };

    return (
        <div className={classNames(className, 'file-upload')} id={id} onBlur={onBlur}>
            <Upload             
                {...rest}
                className={styles.upload}
                id="files"
                ref={uploadRef}
                autoUpload={false}
                batch={false}
                multiple={true}
                files={value}
                defaultFiles={[]}                          
                onAdd={onAdd}
                onRemove={onRemove}
                withCredentials={false}
                restrictions={restrictions}
            />
            <ExternalDropZone
                id="dropzone"
                className={styles.uploadDropzone}
                customHint={hint}
                customNote={note}
                uploadRef={uploadRef}
            />
        </div>
    );
}